import React, { ChangeEvent, FC, ReactNode, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Input } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { addNotification } from "../../../../ducks/notifications/notifications";
import { ResourceMeta as ResourceMetaType, ResourceMeta, ResourceMetaUpdate } from "../../../../hooks/useResourceMeta";
import { extractNamespace, extractResourceName } from "../../../../utils/resourceName";
import { useDialogContext } from "../FullScreenDialog";
import { FieldsContainer, ImageLoader, ImageLoaderContainer, Textarea, Label } from "../styled";
import { FieldsRow } from "./styled";

type EditableMetaProps = {
  children: ReactNode;
  meta?: ResourceMetaType;
  childrenBefore?: boolean;
  onPreviewGenerate?: () => void;
  loading?: boolean;
  showCopyrightAndDescription?: boolean;
  showDescription?: boolean;
  showSystemName?: boolean;
  extra?: ReactNode;
} & ResourceMetaUpdate &
  ResourceMeta;

export const EditableMeta: FC<EditableMetaProps> = ({
  meta,
  setPreview,
  setAlias,
  setDescription,
  onPreviewGenerate,
  setCopyrightText,
  showCopyrightAndDescription,
  showDescription,
  showSystemName,
  setName,
  children,
  childrenBefore,
  loading,
  extra,
}) => {
  const { t } = useTranslation("dialogs");
  const { meta: dialogMeta } = useDialogContext();
  const dispatch = useDispatch();
  const { preview, alias, description, copyrightText, name } = meta;
  const { copyrightLabel, aliasLabel, descriptionLabel, loaderLabel, systemNameLabel } = dialogMeta;

  const removePreview = useCallback(() => setPreview("", null), [setPreview]);

  const updateAlias = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setAlias(e.target.value), [setAlias]);

  const updateDescription = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value),
    [setDescription],
  );

  const updateCopyright = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setCopyrightText(e.target.value),
    [setCopyrightText],
  );

  const updateSystemName = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value) {
        setName(`${extractNamespace(name)}.${e.target.value}`);
      }
    },
    [setName, name],
  );

  const handleImageError = useCallback(() => {
    dispatch(
      addNotification({
        title: t("dialogComponents.fileUploadError"),
        error: true,
        description: `${preview.file.name}. ${t("dialogComponents.tryUploadAgain")}`,
      }),
    );
  }, [preview, t]);

  return (
    <>
      <ImageLoaderContainer>
        {loaderLabel && <Label>{loaderLabel}</Label>}
        <ImageLoader
          showLoadingProgress
          src={(preview && preview.src) || null}
          onUpload={setPreview}
          onGenerateImage={onPreviewGenerate}
          onRemoveRequest={removePreview}
          onError={handleImageError}
          loading={loading || preview.isLoading}
        />
      </ImageLoaderContainer>
      <FieldsContainer>
        {childrenBefore && children}
        {!showCopyrightAndDescription && !showDescription && (
          <>
            {!showSystemName && (
              <Input type="text" label={aliasLabel} value={alias} onChange={updateAlias} width="100%" />
            )}
            {showSystemName && (
              <FieldsRow>
                <Input type="text" label={aliasLabel} value={alias} onChange={updateAlias} width="100%" />
                <Input
                  type="text"
                  label={systemNameLabel}
                  value={extractResourceName(name)}
                  valuePrefix={`${extractNamespace(name)}.`}
                  onChange={updateSystemName}
                  width="100%"
                />
              </FieldsRow>
            )}
            <Textarea
              label={descriptionLabel}
              value={description}
              onChange={updateDescription}
              width="100%"
              height="6.44rem"
              placeholder={""}
            />
          </>
        )}
        {showCopyrightAndDescription && (
          <>
            <Textarea
              label={descriptionLabel}
              value={description}
              onChange={updateDescription}
              width="100%"
              height="6.44rem"
              placeholder={""}
            />
            {!showSystemName && !extra && (
              <Input
                type="text"
                label={copyrightLabel}
                value={copyrightText}
                onChange={updateCopyright}
                width={"100%"}
              />
            )}
            {!showSystemName && extra && (
              <FieldsRow withExtra={!!extra}>
                <Input
                  type="text"
                  label={copyrightLabel}
                  value={copyrightText}
                  onChange={updateCopyright}
                  width={"100%"}
                />
                {extra}
              </FieldsRow>
            )}
            {showSystemName && (
              <FieldsRow>
                <Input
                  type="text"
                  label={systemNameLabel}
                  value={extractResourceName(name)}
                  valuePrefix={`${extractNamespace(name)}.`}
                  onChange={updateSystemName}
                  width="100%"
                />
                <Input
                  type="text"
                  label={copyrightLabel}
                  value={copyrightText}
                  onChange={updateCopyright}
                  width="100%"
                />
              </FieldsRow>
            )}
          </>
        )}
        {showDescription && (
          <Textarea
            label={descriptionLabel}
            value={description}
            onChange={updateDescription}
            width="100%"
            height="4.75rem"
            placeholder={""}
          />
        )}
        {!childrenBefore && children}
      </FieldsContainer>
    </>
  );
};
